/**
 * LiveBadge — compact freshness pill for the document-layer pages
 * (agents/observatory/home). Same `Freshness` enum and the same honesty
 * rule as the 3D field's floor inscription (`freshness-label.ts`, L6c):
 * STALE/OFFLINE/SYNCING never read as LIVE. Server-renderable — no hooks,
 * `lang` is passed in like every other page-level copy object here.
 *
 * The freshness word itself (LIVE/STALE/OFFLINE/SYNCING) is kept as a
 * literal token in both languages, matching `freshnessSuffix` in
 * `freshness-label.ts` — the same word the 3D world and LiveActivity use, so
 * a visitor sees one consistent vocabulary for "is this real" everywhere on
 * the site. Only the surrounding caption and the stale/offline reason are
 * translated.
 */

import type { Freshness, FreshnessReason, LiveMode } from '../../lab/types';
import { showsCanary } from '../../lab/freshness-label';
import type { Lang } from '../../i18n/config';
import s from './LiveBadge.module.css';

export interface LiveBadgeProps {
  lang: Lang;
  freshness: Freshness;
  reason?: FreshnessReason;
  /**
   * Which universe the freshness describes. A `canary` is observer-backed and
   * real, but it is not the flagship population and its decisions may be the
   * engine's code fallback — so the badge says CANARY next to the freshness
   * word rather than reading as plain LIVE.
   */
  mode?: LiveMode | null;
  className?: string;
}

const REASON_TEXT: Record<Lang, Partial<Record<FreshnessReason, string>>> = {
  en: {
    'no progress': 'no progress',
    'provider outage': 'provider outage',
    'epoch boundary': 'epoch boundary',
    'code fallback': 'code fallback',
    unconfigured: 'not configured',
    unreachable: 'observer unreachable',
    'observer error': 'observer error',
    'no run': 'no run',
    'projection error': 'projection error',
  },
  ru: {
    'no progress': 'нет прогресса',
    'provider outage': 'сбой провайдера',
    'epoch boundary': 'граница эпохи',
    'code fallback': 'решения кода',
    unconfigured: 'не настроено',
    unreachable: 'обсервер недоступен',
    'observer error': 'ошибка обсервера',
    'no run': 'нет запуска',
    'projection error': 'ошибка проекции',
  },
};

const ARIA_PREFIX: Record<Lang, string> = {
  en: 'Laboratory freshness',
  ru: 'Свежесть данных лаборатории',
};

/** Spelled out for assistive technology; the pill itself shows the short token. */
const CANARY_ARIA: Record<Lang, string> = {
  en: 'engineering canary, code fallback possible',
  ru: 'инженерная канарейка, возможны решения кода',
};

const STATE_CLASS: Record<Freshness, string> = {
  LIVE: s.live,
  STALE: s.stale,
  OFFLINE: s.offline,
  SYNCING: s.syncing,
};

export function reasonText(lang: Lang, reason: FreshnessReason | undefined): string | null {
  if (reason === undefined) return null;
  return REASON_TEXT[lang][reason] ?? reason;
}

export default function LiveBadge({ lang, freshness, reason, mode, className }: LiveBadgeProps) {
  const reasonLabel = reasonText(lang, reason);
  const canary = showsCanary(freshness, mode);
  return (
    <span
      className={`${s.badge} ${STATE_CLASS[freshness]} ${canary ? s.canary : ''} ${className ?? ''}`}
      aria-label={`${ARIA_PREFIX[lang]}: ${freshness}${reasonLabel ? ` (${reasonLabel})` : ''}${
        canary ? ` — ${CANARY_ARIA[lang]}` : ''
      }`}
    >
      <span className={s.dot} aria-hidden="true" />
      <span className="machine machine--sm">{freshness}</span>
      {reasonLabel && <span className={`machine machine--sm ${s.reason}`}>{reasonLabel}</span>}
      {canary && <span className={`machine machine--sm ${s.mode}`}>CANARY</span>}
    </span>
  );
}
